import React, { useMemo } from 'react';
import { useGetCategoryTreeQuery } from './apiCategoryTree';
import TreeLeaf from './TreeLeaf';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../store';
import { isRootCategory, removeCatEnabledException } from '../../../lib/categories/categoriesLib';
import { ROOT_CATEGORY_TITLE } from '../_constants';
import { useCategoryConfiguration } from '../../../hooks/useCategoryConfiguration';
import { ConfigurationFieldType, SkuType } from '@ydistri/api-sdk';
import { CategoryTreePresentationMode } from './CategoryTreeTypes';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

interface TreeCategoryProps {
  categoryId: number;
  onCategoryChange?: (event: React.MouseEvent, categorySlug: string) => void;
  search: string;
  presentationMode?: CategoryTreePresentationMode;
}

const TreeCategory: React.FC<TreeCategoryProps> = ({
  categoryId,
  onCategoryChange,
  search,
  presentationMode = CategoryTreePresentationMode.CONFIGURATION,
}) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: categories, isFetching } = useGetCategoryTreeQuery(templateOrCalculation.value);

  const category = useMemo(() => {
    return categories?.[categoryId];
  }, [categories, categoryId]);

  const { categoryConfigurationData: configuration } = useCategoryConfiguration(categoryId);
  // eslint-disable-next-line typescript/no-unnecessary-condition -- configuration may be undefined for manual calculations
  const configData = configuration?.[ConfigurationFieldType.CatEnableCategory]?.[SkuType.FastMover];
  const isCategoryDisabled = configData?.value === '0'; // Treat undefined as enabled (manual calculations do not have configuration value)

  const isOpened = useSelector(
    (state: ReduxState) => !!state.categoryTreeReducer.openedCategories[category?.slug ?? '0'],
  );

  const selectedCategory = useSelector(
    (state: ReduxState) =>
      state.categoryTreeReducer.selectedCategory[category?.slug ?? '0'] ?? false,
  );

  if (!category) return null;

  if (isFetching) {
    return <Spin spinning={true} />;
  }

  const cleanExceptions = removeCatEnabledException(category.exceptions);
  const hasException = cleanExceptions.some(e => !e.hidden);
  const title = isRootCategory(category.id) ? ROOT_CATEGORY_TITLE : category.title;

  return (
    <>
      <TreeLeaf
        slug={category.slug}
        categoryId={category.id}
        title={title}
        selectionType={selectedCategory}
        opened={isOpened}
        hasException={hasException}
        subcategoryHasException={category.childExceptionCount > 0}
        level={category.level}
        lastLevel={category.children.length === 0}
        onCategoryChange={onCategoryChange}
        search={search}
        disabled={isCategoryDisabled}
        ownValue={configData?.overwritten}
        presentationMode={presentationMode}
      />
      {isOpened &&
        category.children.map(subCategoryId => (
          <TreeCategory
            key={subCategoryId}
            categoryId={subCategoryId}
            onCategoryChange={onCategoryChange}
            search={search}
            presentationMode={presentationMode}
          />
        ))}
    </>
  );
};

const MemoTreeCategory = React.memo(TreeCategory);
MemoTreeCategory.displayName = 'TreeCategory';

export default MemoTreeCategory;
